<template>
  <div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'expenses-dashboard', params: { teamId: activeTeam.id } }">
          {{ $tc('expense', 2) }}
        </router-link>
        / <span class="text-weight-bold">{{ $t('capture_expense') }}</span>

        <q-chip
          v-if="isCommitted"
          class="on-right status-chip"
          color="positive"
          small
        >
          <div class="row items-center no-wrap">
            <q-icon name="lock" size="1rem"/>

            <span class="on-right">{{ $t('committed') }}</span>
          </div>
        </q-chip>
      </q-toolbar-title>

      <div v-if="expenseLoaded">
        <q-btn
          v-if="!isCommitted"
          flat
          icon="lock"
          class="text-grey-8 q-mr-md"
          @click="handleCommit"
        >
          <q-tooltip>
            {{ $t('commit') }}
          </q-tooltip>
        </q-btn>

        <q-btn
          color="primary"
          class="q-mr-md"
          flat
          icon="delete"
          :disable="isCommitted"
          @click="handleDelete"
        >
          {{ $t('delete') }}
        </q-btn>

        <q-btn
          color="primary"
          @click="handleSave"
        >
          {{ $t('save') }}
        </q-btn>
      </div>
    </q-toolbar>
    <div class="layout-padding" style="min-height: calc(100vh - 60px - 70px)">
      <div v-if="expenseLoaded" class="row full-height">
        <div class="col-12 col-md-6">
          <document-viewer v-if="document" :document="document" :showDownloadButton="true" />
        </div>
        <div class="col-12 col-md-6">
          <expense-form v-model="formData"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Dialog, date } from 'quasar'
  import { get, isNil, omitBy } from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import DocumentViewer from 'components/Documents/DocumentViewer.vue'
  import ExpenseForm from 'components/Expenses/ExpenseForm.vue'
  import { ExpensesClient } from 'src/api/Expenses'
  import { createErrorFromResponse } from 'src/utils/error'
  import i18n from 'src/i18n/i18n'
  import parseDate from 'date-fns/parse'
  import { snakeCase } from 'change-case-object'

  const includes = [
    'supplier', 'document', 'document.documentRecognition', 'document.documentRecognition.customer'
  ]

  export default {
    $_veeValidate: {
      validator: 'new'
    },
    components: {
      DocumentViewer,
      ExpenseForm
    },
    data () {
      return {
        expense: null,
        expenseLoaded: false,
        formData: null
      }
    },
    computed: {
      ...mapGetters(['activeTeam']),
      document () {
        if (this.expense === null) {
          return null
        }

        return this.expense.document.data
      },
      isCommitted () {
        return this.formData && this.formData.committedAt !== null
      }
    },
    methods: {
      ...mapActions(['commitExpense', 'deleteExpense']),
      resetFormData () {
        this.formData = null
      },
      handleResponse (response) {
        const expense = response.data.data[0]
        this.expense = expense
        this.formData = this.loadFormDataFromExpense(expense)
        this.expenseLoaded = true
      },
      loadFormDataFromExpense (expense) {
        const recognition = get(expense, 'document.data.documentRecognition.data', null)

        const invoiceDate = expense.invoice_date
          ? expense.invoice_date.date
          : get(recognition, 'result.entities.issuedAt', null)

        return {
          supplier: expense.supplier ? expense.supplier.data : get(recognition, 'customer.data', null),
          invoiceDate: invoiceDate ? parseDate(invoiceDate) : null,
          deliveryDate: expense.delivery_date ? parseDate(expense.delivery_date.date) : null,
          accountingPeriod: expense.accounting_period ? parseDate(expense.accounting_period.date) : null,
          invoiceNumber: expense.invoice_number || get(recognition, 'result.entities.number', null),
          totalNet: this.getTotalNet(expense, recognition),
          currency: expense.currency || 'EUR',
          salesTaxAmount: expense.lines ? expense.lines[0]['tax_amount'] : null,
          salesTaxRate: expense.lines ? expense.lines[0]['tax_rate'] : null,
          cashDiscount: expense.cash_discount,
          cashDiscountLimit: expense.cash_discount_limit ? parseDate(expense.cash_discount_limit.date) : null,
          dueDate: expense.due_date ? parseDate(expense.due_date.date) : null,
          paidAt: expense.last_payment_at ? parseDate(expense.last_payment_at.date) : null,
          committedAt: expense.committed_at ? parseDate(expense.committed_at) : null,
          projectId: expense.project_id
        }
      },
      getTotalNet (expense, recognition) {
        if (expense.total_net) {
          return expense.total_net
        }

        const rec = get(recognition, 'result.entities.totals.net', null)

        return rec === null ? null : Math.floor(rec * 100)
      },
      handleSave () {
        this.$validator.validateAll()
          .then(result => {
            if (result) {
              this.updateExpense()
            }
          })
      },
      handleCommit () {
        this.commitExpense({ expenseId: this.expense.id })
          .then(() => {
            this.formData.committedAt = parseDate(this.expense.committed_at)
          })
      },
      updateExpense () {
        const data = {
          ...snakeCase(this.formData),
          invoice_date: this.formData.invoiceDate
            ? date.formatDate(this.formData.invoiceDate, 'YYYY-MM-DD')
            : null,
          delivery_date: this.formData.deliveryDate
            ? date.formatDate(this.formData.deliveryDate, 'YYYY-MM-DD')
            : null,
          due_date: this.formData.dueDate
            ? date.formatDate(this.formData.dueDate, 'YYYY-MM-DD')
            : null,
          accounting_period: this.formData.accountingPeriod
            ? date.formatDate(this.formData.accountingPeriod, 'YYYY-MM-DD')
            : null,
          cash_discount_limit: this.formData.cashDiscountLimit
            ? date.formatDate(this.formData.cashDiscountLimit, 'YYYY-MM-DD')
            : null,
          'supplier_id': this.formData.supplier ? this.formData.supplier.id : null,
          'lines': !isNil(this.formData.salesTaxRate) && !isNil(this.formData.salesTaxAmount)
            ? [ { 'total_net': this.formData.totalNet, 'tax_rate': this.formData.salesTaxRate, 'tax_amount': this.formData.salesTaxAmount } ]
            : null,
          complete: true
        }

        delete data.supplier
        delete data.sales_tax_rate
        delete data.sales_tax_amount

        const cleanedData = omitBy(data, isNil)

        // Always set project_id to allow to delete it
        cleanedData.project_id = data.project_id

        this.updatePayments()
          .then(() => {
            if (this.isCommitted) {
              return
            }

            return new ExpensesClient()
              .update(this.expense.id, cleanedData)
          })
          .then(() => {
            this.$q.notify({ message: this.$t('expense_saved_success'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },
      updatePayments (forceDelete = false) {
        const originalPaidAt = this.expense.last_payment_at ? parseDate(this.expense.last_payment_at.date).getTime() : null
        const formPaidAt = this.formData.paidAt ? parseDate(this.formData.paidAt).getTime() : null

        if (originalPaidAt === formPaidAt) {
          return Promise.resolve()
        }

        const client = new ExpensesClient()
        let prom

        if (originalPaidAt !== null || forceDelete) {
          prom = client
            .include('payments')
            .get(this.expense.id)
            .then(response => {
              const data = response.data.data[0]

              if (!data.payments) return

              return Promise.all(
                data.payments.data.map(p => client.destroyPayment(this.expense.id, p.id))
              )
            })
        } else {
          prom = Promise.resolve()
        }

        return prom.then(() => {
          client.addPayment(this.expense.id, {
            payment_date: date.formatDate(this.formData.paidAt, 'YYYY-MM-DD'),
            balance: this.formData.balance
          })
        }).catch(e => {
          // If a payment was created in another tab we experience a race-condition
          // To solve this we have to delete all old payments before we create the
          // new one. Since this is not necessary in most cases we try to skip
          // that step if we think there is no payment yet. If the payment creation
          // fails we delete all payments and retry once.
          if (!forceDelete) {
            return this.updatePayments(true)
          }

          throw e
        })
      },
      handleDelete () {
        Dialog.create({
          title: i18n.t('delete_expense_dialog_title'),
          message: i18n.t('delete_expense_dialog_body'),
          color: 'primary',
          ok: i18n.t('delete'),
          cancel: i18n.t('cancel')
        })
          .then(() => {
            this.deleteExpense({ expenseId: this.expense.id })
              .then(() => {
                this.$router.push({ name: 'expenses-list', params: { teamId: this.activeTeam.id } })
              })
              .catch(createErrorFromResponse)
          })
          .catch(() => {})
      }
    },
    beforeRouteEnter (to, from, next) {
      const expenseId = to.params.expenseId

      new ExpensesClient()
        .include(includes)
        .get(expenseId)
        .then((response) =>
          next((vm) => vm.handleResponse(response))
        )
    },
    beforeRouteUpdate (to, from, next) {
      const expenseId = to.params.expenseId

      new ExpensesClient()
        .include(includes)
        .get(expenseId)
        .then(this.handleResponse)
    }
  }
</script>
